import "./ProyectosCommon.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { CosechaResponse } from "../../../shared/response/proyectos/CosechaResponse";
import { CosechaPayload, CosechaCategoriaTasaInputPayload } from "../../../shared/services/api/payload/proyectos/CosechaPayload";
import { CosechaSaveService } from "../../services/proyectos/CosechaSaveService";

export class CosechaFormModal extends BaseSmallModalFormComponent<
  CosechaResponse,
  CosechaPayload,
  CosechaSaveService
> {
  private _nombre!:  HTMLInputElement;
  private _anio!:    HTMLInputElement;
  private _tasaBase!: HTMLInputElement;
  private _fechaIni!: HTMLInputElement;
  private _fechaFin!: HTMLInputElement;

  constructor(
    private readonly _categorias: string[],
    private readonly _onSavedCb: (item: CosechaResponse) => void,
    private readonly _item?: CosechaResponse,
  ) {
    super(new CosechaSaveService());
  }

  protected get title(): string { return this._item ? "Editar cosecha" : "Nueva cosecha"; }
  protected get saveText(): string { return "Guardar"; }

  private _tasaDe(categoria: string): number {
    const existente = this._item?.tasasPorCategoria.find((t) => t.categoria === categoria);
    return existente ? existente.tasa : (this._item?.tasaBaseIpc ?? 0);
  }

  protected renderBody(): string {
    const c = this._item;
    const anioSugerido = c?.anio ?? new Date().getFullYear();
    return `
      <div class="cl-form-fields">
        <div class="pr-form-row">
          <div class="cl-form-group">
            <label class="cl-form-label" for="cos-nombre">Nombre <span class="cl-required">*</span></label>
            <input id="cos-nombre" class="sx-input" type="text" maxlength="100" value="${this._esc(c?.nombre ?? `Cosecha ${anioSugerido}`)}" placeholder="Cosecha 2026">
          </div>
          <div class="cl-form-group">
            <label class="cl-form-label" for="cos-anio">Año <span class="cl-required">*</span></label>
            <input id="cos-anio" class="sx-input sx-input--numeric" type="number" min="2020" max="2100" step="1" value="${anioSugerido}">
          </div>
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="cos-tasabase">Tasa IPC base (%) <span class="cl-required">*</span></label>
          <input id="cos-tasabase" class="sx-input sx-input--numeric" type="number" min="0" max="100" step="0.1" value="${((c?.tasaBaseIpc ?? 0) * 100).toFixed(1)}">
          <span class="pr-form-note">Se aplica a toda categoría sin ajuste individual abajo.</span>
        </div>
        <div class="pr-form-row">
          <div class="cl-form-group">
            <label class="cl-form-label" for="cos-inicio">Inicio de vigencia</label>
            <input id="cos-inicio" class="sx-input" type="date" value="${c?.fechaInicioVigencia ? c.fechaInicioVigencia.slice(0, 10) : ""}">
          </div>
          <div class="cl-form-group">
            <label class="cl-form-label" for="cos-fin">Fin de vigencia</label>
            <input id="cos-fin" class="sx-input" type="date" value="${c?.fechaFinVigencia ? c.fechaFinVigencia.slice(0, 10) : ""}">
          </div>
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label">Ajuste por categoría</label>
          <span class="pr-form-note" style="display:block;margin-bottom:6px">Sobreescribe la tasa base para categorías con comportamiento de precio distinto.</span>
          ${this._categorias.length === 0
            ? `<div class="pr-empty" style="padding:12px">Sin categorías en el catálogo todavía.</div>`
            : `<div style="max-height:260px;overflow-y:auto;border:1px solid var(--sx-border-light);border-radius:var(--sx-radius-md)">
                ${this._categorias.map((cat) => `
                  <div style="display:flex;align-items:center;justify-content:space-between;padding:7px 10px;border-bottom:1px solid var(--sx-border-light)">
                    <span style="font-size:var(--sx-text-sm)">${this._esc(cat)}</span>
                    <div style="display:flex;align-items:center;gap:4px">
                      <input class="sx-input sx-input--numeric" style="width:70px;padding:4px 6px" type="number" min="0" max="100" step="0.1"
                        data-cat="${this._esc(cat)}" value="${(this._tasaDe(cat) * 100).toFixed(1)}">
                      <span style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">%</span>
                    </div>
                  </div>`).join("")}
              </div>`}
        </div>
      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._nombre    = el.querySelector<HTMLInputElement>("#cos-nombre")!;
    this._anio      = el.querySelector<HTMLInputElement>("#cos-anio")!;
    this._tasaBase  = el.querySelector<HTMLInputElement>("#cos-tasabase")!;
    this._fechaIni  = el.querySelector<HTMLInputElement>("#cos-inicio")!;
    this._fechaFin  = el.querySelector<HTMLInputElement>("#cos-fin")!;

    this._nombre.addEventListener("input", () => this._validate());
    this._anio.addEventListener("input", () => this._validate());
    this._tasaBase.addEventListener("input", () => this._validate());
    this._validate();
  }

  protected buildPayload(): CosechaPayload {
    const tasasPorCategoria: CosechaCategoriaTasaInputPayload[] = this._categorias.map((cat) => {
      const input = this.getElement().querySelector<HTMLInputElement>(`input[data-cat="${cat}"]`);
      const pct = input ? Number(input.value) || 0 : (Number(this._tasaBase.value) || 0);
      return { categoria: cat, tasa: pct / 100 };
    });

    return {
      id: this._item?.id ?? null,
      nombre: this._nombre.value.trim(),
      anio: Number(this._anio.value) || new Date().getFullYear(),
      tasaBaseIpc: (Number(this._tasaBase.value) || 0) / 100,
      fechaInicioVigencia: this._fechaIni.value ? new Date(this._fechaIni.value).toISOString() : null,
      fechaFinVigencia: this._fechaFin.value ? new Date(this._fechaFin.value).toISOString() : null,
      tasasPorCategoria,
    };
  }

  protected override onSaved(item: CosechaResponse): void {
    this._onSavedCb(item);
    this.dispose();
  }

  private _validate(): void {
    const ok = this._nombre.value.trim().length >= 1
      && Number(this._anio.value) >= 2020
      && this._tasaBase.value.trim() !== "" && Number(this._tasaBase.value) >= 0;
    this.setSaveDisabled(!ok);
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
